import React, { useEffect, useMemo, useState } from 'react';

import { useHistory } from 'react-router';
import { t } from 'i18next';
import classNames from 'classnames';

import Input from 'components/Input/Text/Input';
import './DirectMessages.scoped.scss';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { getActiveChat, isInitialChat } from 'redux/chat/selectors';
import { addActiveRoom, cleanChatRoom } from 'redux/chat/actions';
import { getUser } from 'redux/authentication/auth.slice';
import socket from 'domain/services/socketIoTemp';
import {
  selectParticipants,
  fetchUsers,
  markAllMassagesAsRead,
} from 'redux/chat/direct_messages.slices';
import { selectActiveUsers } from 'redux/chat/active_status.slices';
import { changeCurrentScreen } from 'redux/mobile_screen_flow/mobile_screen_flow.slice';
import useDeviceDetect from 'hooks/useDeviceDetect';
import { selectUserData } from 'redux/editProfile/userProfile.slices';
import ProfilePicture from 'clean/components/common/ProfilePicture/ProfilePicture';

interface Props {
  children?: React.ReactNode;
}

const DirectMessages: React.FC<Props> = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const initialChat = useAppSelector(isInitialChat);
  const activeChat = useAppSelector(getActiveChat);
  const participants = useAppSelector(selectParticipants);
  const activeUsers = useAppSelector(selectActiveUsers);
  const user = useAppSelector(getUser);
  const { isMobile } = useDeviceDetect();
  const [filter, setFilter] = useState('');
  const userData = useAppSelector(selectUserData);

  const selfParticipant = {
    user: {
      avatar_url: userData?.avatar_url,
      full_name: `${userData?.first_name} ${userData?.last_name}`,
      id: userData?.id,
    },
  };

  const mapParticipantsActiveStatus = (id: string) => {
    return activeUsers.find(e => e === id);
  };

  const selectRoom = async participant => {
    if (activeChat) {
      socket.emit('leaveChat', activeChat.id);
      dispatch(cleanChatRoom());
    }
    socket.emit('joinChat', participant.id);
    const fixObj = {
      __typename: 'chats',
      avatar_url: participant.user.avatar_url,
      participants: [selfParticipant, participant],
      id: participant.id,
      title: participant.user?.full_name,
      user: participant,
    };
    dispatch(markAllMassagesAsRead(participant.id));
    dispatch(addActiveRoom(fixObj));
    history.push({ pathname: '/chat', search: `id=${participant.id}` });
  };

  useEffect(() => {
    dispatch(fetchUsers(user.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedDirect = participants.find(direct => direct.id === activeChat?.id);
  useEffect(() => {
    if (selectedDirect) {
      selectRoom(selectedDirect);
    }
  }, []);
  useEffect(() => {
    if (participants && participants.length > 0 && initialChat) {
      selectRoom(participants[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participants, initialChat]);

  const participantsFiltered = useMemo(() => {
    return participants?.filter(e => e.user?.full_name?.toLowerCase().includes(filter));
  }, [participants, filter]);

  const goToChat = participant => {
    if (activeChat.id !== participant.id) {
      selectRoom(participant);
      isMobile && dispatch(changeCurrentScreen('chat'));
    }
  };

  return (
    <div className="dirrect-messages">
      <div className="direct-input">
        <Input
          onChange={e => setFilter(e)}
          type="text"
          style={{
            paddingBottom: '0px',
            paddingTop: '0px',
            height: '37px',
          }}
          placeholder={t('common:direct_messages')}
        />
      </div>
      <div className="people">
        {participantsFiltered &&
          participantsFiltered.map(
            participant =>
              participant.id !== user.id && (
                <div
                  key={participant.id}
                  onClick={() => goToChat(participant)}
                  className={classNames('people-item pointer', {
                    active: activeChat && activeChat.title === participant?.user?.full_name,
                  })}
                >
                  <span
                    className={classNames('status-dot', {
                      'active-user': mapParticipantsActiveStatus(participant.user.id),
                    })}
                  />
                  <ProfilePicture userId={participant.user.id} />
                  <p
                    className={classNames('participent-name', {
                      'active-title': mapParticipantsActiveStatus(participant.user.id),
                    })}
                    title={participant.user?.full_name}
                  >
                    {participant.user?.full_name}
                  </p>
                  {participant.unread_messages > 0 && (
                    <div className="unread-messages">
                      <span>{participant.unread_messages}</span>
                    </div>
                  )}
                </div>
              )
          )}
      </div>
    </div>
  );
};

export default DirectMessages;
